import { createTheme } from '@mui/material/styles';
import { SLICE_NAME } from 'constants/common';
import { THEME_KEY } from 'constants/theme';
import store from 'store/store';
import { normalizeColors } from 'utils/functions';

const generateTheme = (theme) => {
    const draftConfig = store.getState()[SLICE_NAME.THEME]?.draftConfig;
    const c = normalizeColors({
        primary: theme.primary,
        secondary: theme.secondary,
        text: theme.text,
        isVMPTheme: draftConfig?.[THEME_KEY.IS_DEFAULT] ?? true,
    });

    return createTheme({
        palette: {
            primary: {
                90: c.P3,
                800: c.M1, // mix
                900: c.P1,
                1000: c.PS,
                main: c.P0,
                light: c.P2,
                dark: c.PP,
            },
            secondary: {
                main: c.S0,
                light: c.S1,
            },
            text: {
                50: c.T4,
                700: c.T1, // label
                primary: c.T0, // content
                secondary: c.T2,
                disabled: c.T3,
            },
            ui: {
                bgcolor: '#E9E9E9',
                hover: '#E9E9E9',
                disabled: '#E9E9E9',
                border: '#E9E9E9',
                avatarBgcolor: '#bdbdbd',
                textDisabled: '#8F9294',
            },
        },
        typography: {
            fontFamily: `"${theme.font}", Arial, sans-serif`,
        },
        components: {
            MuiPagination: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '& div': {
                            borderRadius: '6px',
                            border: `1px solid #AAAAAA`,
                            height: '32px',
                        },
                        '& .MuiButtonBase-root.MuiPaginationItem-root': {
                            borderRadius: '6px',
                            border: `1px solid #AAAAAA`,
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: theme.palette.ui.hover,
                            },
                        },
                        '& .MuiButtonBase-root.MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            color: '#FFFFFF',
                            borderColor: theme.palette.primary[900],
                        },
                    }),
                },
            },
            MuiMenu: {
                styleOverrides: {
                    root: {
                        '& .MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
                            overflow: 'hidden',
                        },
                    },
                },
            },
            MuiBottomNavigationAction: {
                styleOverrides: {
                    root: {
                        '& .Mui-selected': {
                            color: '#C4132B',
                        },
                    },
                },
            },
            MuiBottomNavigation: {
                styleOverrides: {
                    root: {
                        height: '48px',
                    },
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        minHeight: '48px',
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    root: {
                        '& .MuiPaper-root.MuiPopover-paper': {
                            overflow: 'initial',
                            marginTop: '12px',
                            borderRadius: '16px',
                        },
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: theme.palette.text.primary,
                    }),
                },
            },
            MuiTextField: {
                defaultProps: {
                    size: 'small',
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        variants: [
                            {
                                props: { fullWidth: true },
                                style: {
                                    width: '100%',
                                },
                            },
                        ],
                        width: '256px',

                        '& .MuiInputBase-input': {
                            paddingTop: '6.5px',
                            paddingBottom: '6.5px',
                        },
                        '& .MuiInputAdornment-root.MuiInputAdornment-positionStart:has(button)': {
                            marginLeft: '-8px',
                            marginRight: '4px',
                        },
                        '& .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
                            marginLeft: '-4px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            // borderColor: '#8F9294',
                        },
                        '& .MuiInputBase-root': {
                            fontWeight: '500',
                            borderRadius: '6px',
                            backgroundColor: 'white',
                            // overflow: 'hidden',
                            '& fieldset': {
                                // marginTop: '1px'
                            },
                            '&:hover': {
                                '& fieldset': {
                                    borderColor: theme.palette.secondary.light,
                                },
                            },
                            '&.Mui-focused': {
                                '&.Mui-error': {
                                    '& fieldset': {
                                        // borderColor: '#B3261E'
                                    },
                                },
                                '& fieldset': {
                                    // borderColor: theme.palette.focus.main,
                                    borderWidth: '1px',
                                },
                            },
                            '& input::placeholder': {
                                fontSize: '16px',
                                opacity: '1',
                            },
                            '&.Mui-error': {
                                '& fieldset': {
                                    // borderColor: '#B3261E'
                                },
                            },
                            '&:has(.MuiButtonBase-root)': {
                                paddingRight: 0,

                                '& .MuiButtonBase-root': {
                                    padding: 0,
                                    marginRight: '12px',
                                },
                            },
                        },
                        '& .MuiFormHelperText-root.Mui-error': {
                            // color: '#B3261E',
                            fontSize: '14px',
                            lineHeight: '21px',
                        },
                    }),
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '&:hover': {
                            '& fieldset': {
                                borderColor: `${theme.palette.secondary.light} !important`,
                            },
                        },
                        '& .MuiInputBase-root': {
                            borderRadius: '6px',
                        },
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                            border: 0,
                        },
                        '&.Mui-disabled': {
                            backgroundColor: theme.palette.ui.disabled,
                            color: theme.palette.text.disabled,
                        },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            // borderColor: '#B3261E',
                        },
                        '&.Mui-focused fieldset': {
                            // borderColor: theme.palette.focus.main,
                        },
                    }),
                },
            },
            MuiTable: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        // borderRadius: 8,
                        // overflow: 'hidden'
                    }),
                },
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        '& .MuiTableRow-root:nth-of-type(even)': {
                            backgroundColor: 'rgb(240, 240, 240, 0.5)',
                        },
                        '& .MuiTableRow-root:nth-of-type(odd)': {
                            backgroundColor: '#FFFFFF',
                        },
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        height: '48px',
                        '&.Mui-selected': {
                            backgroundColor: `${theme.palette.primary[90]} !important`,
                        },
                        '&.MuiTableRow-hover:hover': {
                            backgroundColor: '#F0F0F0',
                        },
                    }),
                },
            },
            MuiTableCell: {
                defaultProps: {
                    size: 'small',
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        padding: '4px 12px',
                        fontSize: '15px',
                        '&.MuiTableCell-head': {
                            borderTop: '1px solid #d1d1d1',
                            boxShadow: '0px 3px #d1d1d1',
                            fontWeight: 700,
                            backgroundColor: theme.palette.primary[90],
                            borderLeft: '1px solid #E0E0E0',
                            borderBottom: 'none !important',
                            fontSize: '16px',
                        },
                        '& .MuiTypography-root': {
                            fontSize: '15px',
                        },
                    }),
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        minHeight: 'unset',
                        '& .MuiTabs-flexContainer': {
                            height: '60px',
                            alignItems: 'center'
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: theme.palette.primary.main,
                            bottom: '0',
                        },
                    }),
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: '18px',
                        fontWeight: 700,
                        textTransform: 'unset',
                        padding: 0,
                        minHeight: 'unset',
                        height: '36px',
                        color: theme.palette.text.primary,
                        '&.Mui-selected': {
                            color: theme.palette.primary.main,
                        },
                        '& + .MuiTab-root': {
                            marginLeft: '40px',
                        },
                    }),
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '&:hover': {
                            backgroundColor: 'rgb(68 73 77 / 22%)',
                            opacity: 0.9,
                        },
                    }),
                },
            },
            MuiButtonBase: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '&.MuiPickersDay-root': {
                            borderRadius: '4px',
                            fontWeight: '600',
                            '&:hover': {
                                backgroundColor: theme.palette.ui.hover,
                            },
                            '&.Mui-selected': {
                                fontWeight: '600',
                                color: '#fff',
                                backgroundColor: theme.palette.primary.main,
                                transition: 'auto',
                                '&:hover': {
                                    backgroundColor: theme.palette.ui.hover,
                                },
                            },
                            '&.MuiPickersDay-today': {
                                backgroundColor: '#FCE5EA',
                                border: 0,
                                position: 'relative',
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.ui.hover,
                                },
                                '&::after': {
                                    content: '""',
                                    display: 'block',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '10px solid transparent' /* Độ rộng của tam giác */,
                                    borderTop: '10px solid transparent' /* Độ rộng của tam giác */,
                                    borderBottom: '10px solid #ee0033' /* Màu và chiều cao của tam giác */,
                                    position: 'absolute',
                                    bottom: '2px',
                                    right: '2px',
                                },
                            },
                        },
                    }),
                },
            },
            MuiPickersYear: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '& .MuiPickersYear-yearButton.Mui-selected': {
                            borderRadius: '4px',
                            backgroundColor: theme.palette.primary.main,
                        },
                    }),
                },
            },
            MuiDialog: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        variants: [
                            {
                                props: {
                                    fullWidth: true,
                                    maxWidth: 'sm',
                                },
                            },
                        ],

                        '& .MuiPaper-root': {
                            // maxWidth: 'unset',
                            boxShadow: '0px 0px 10px 4px #00000040',
                            borderRadius: '20px',
                            // width: 'fit-content'
                        },
                    }),
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        [theme.breakpoints.down('sm')]: {
                            // width: 'auto',
                            // minWidth: '320px'
                        },
                        [theme.breakpoints.up('sm')]: {
                            // minWidth: '400px',
                            // maxWidth: '500px'
                        },
                        padding: '8px 40px',
                    }),
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        [theme.breakpoints.down('sm')]: {
                            width: 'auto',
                            minWidth: '320px',
                        },
                        // borderTop: '1px solid #D1D1D1',
                        justifyContent: 'end',
                        padding: '8px 40px 40px 40px',
                    }),
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        // boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.1), 0px 2px 2px 0px rgba(0,0,0,0.1), 0px 1px 5px 0px rgba(0,0,0,0.1)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '24px 40px 4px 40px',
                    },
                },
            },
            MuiDialogContentText: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: theme.palette.text.primary,
                    }),
                },
            },
            MuiContainer: {
                styleOverrides: {
                    root: {
                        padding: '0 !important',
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: theme.palette.text[700],
                        marginBottom: '6px',
                        lineHeight: '20px',
                        height: '20px',

                        '&.Mui-focused': {
                            color: theme.palette.text.primary,
                        },

                        '& .MuiTypography-root': {
                            color: theme.palette.text[700],
                        },
                    }),
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        padding: '6px',
                    },
                },
            },
            // MuiSwitch: {
            //     styleOverrides: {
            //         root: ({ theme }) => ({
            //             '& .MuiSwitch-colorPrimary': {
            //                 '& .MuiSwitch-thumb': {
            //                     backgroundColor: '#fff',
            //                     border: '1px solid #949494',
            //                 },
            //             },
            //             '& .Mui-checked': {
            //                 '& .MuiSwitch-thumb': {
            //                     backgroundColor: theme.palette.primary.main,
            //                     border: '1px solid #fff',
            //                 },
            //             },
            //         }),
            //     },
            // },
            MuiSelect: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontWeight: '500',
                        borderRadius: '6px',
                        '& > div': {
                            padding: '6.5px 14px',
                        },
                        '& fieldset': {
                            // borderColor: theme.palette.secondary.light,
                        },
                        '&:hover': {
                            '& fieldset': {
                                // [NOTE]
                                borderColor: theme.palette.secondary.light,
                            },
                        },
                        '&.Mui-focused': {
                            '&.Mui-error': {
                                '& fieldset': {
                                    borderColor: '#B3261E',
                                },
                            },
                            '& fieldset': {
                                // [NOTE]
                                borderColor: `${theme.palette.secondary.light} !important`,
                            },
                        },
                    }),
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '&:hover': {
                            color: theme.palette.primary.main,
                            backgroundColor: '#F0F0F0',
                            '& label': {
                                '& span:nth-child(2)': {
                                    color: theme.palette.primary.main,
                                },
                            },
                        },
                        '& .MuiFormControlLabel-root': {
                            '& .MuiTypography-root': {
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: '#F0F0F0',
                                },
                            },
                        },
                    }),
                },
            },
            MuiPopper: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '& .MuiAutocomplete-option.Mui-focused': {
                            color: theme.palette.primary.main,
                        },
                    }),
                },
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        '& .MuiFormHelperText-root': {
                            marginLeft: '0px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            // borderColor: '#8F9294'
                        },
                        '& .MuiFormLabel-asterisk': {
                            color: '#ee0033',
                        },
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    root: {
                        '& .MuiInputBase-root.MuiOutlinedInput-root': {
                            paddingTop: '4px',
                            paddingBottom: '4px',

                            '& .MuiAutocomplete-endAdornment': {
                                right: '-4px'
                            },

                            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid rgba(0, 0, 0, 0.23)'
                            }
                        },
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        '& span': {
                            padding: '0',
                        },
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        rowGap: '2px !important',
                        variants: [
                            {
                                props: { labelPlacement: 'top' },
                                style: {
                                    margin: 0,
                                    width: '100%',
                                    alignItems: 'self-start',
                                    rowGap: '8px',
                                },
                            },
                        ],
                        '& .MuiFormControlLabel-label': {
                            fontSize: '16px',
                            fontWeight: '500',
                            color: theme.palette.text[700],

                            '& .MuiTypography-root': {
                                color: theme.palette.text[700],
                            },

                            '&.Mui-disabled': {
                                color: theme.palette.ui.textDisabled,
                            },

                            '&.Mui-disabled p': {
                                color: theme.palette.ui.textDisabled,
                            },
                        },
                        '& .MuiFormControlLabel-asterisk': {
                            color: '#ee0033',
                        },
                        '& .textarea-auto-size': {
                            width: '100%',

                            '& .MuiFormHelperText-root.Mui-error': {
                                color: '#B3261E',
                                fontSize: '14px',
                                lineHeight: '21px',
                                // fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                                margin: 0,
                            },
                            '&::placeholder': {
                                color: theme.palette.text.secondary,
                            },
                        },
                        '&.MuiFormControlLabel-labelPlacementStart': {
                            margin: 0,
                            width: '100%',
                            columnGap: '24px',
                            justifyContent: 'flex-end',
                            '& .MuiStack-root': {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                                flex: 1,
                                alignSelf: 'flex-start',
                                minWidth: '200px',

                                '& .MuiFormControlLabel-label': {
                                    lineHeight: '36px',
                                },

                                '& .MuiFormControlLabel-asterisk': {
                                    lineHeight: '10px'
                                }
                            },
                            '& .MuiFormControlLabel-label': {
                                display: 'flex',
                                // flex: 1,
                                justifyContent: 'flex-end',
                                minWidth: '200px',
                            },
                        }
                    }),
                },
            },
            MuiGrid: {
                styleOverrides: {
                    root: {},
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '& input::placeholder': {
                            color: theme.palette.text.secondary,
                            opacity: '1',
                        },
                        '& textarea::placeholder': {
                            color: theme.palette.text.secondary,
                            opacity: '1',
                        },
                        '&.MuiOutlinedInput-root.Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
                            borderWidth: 1,
                        },
                    }),
                },
            },
            MuiButton: {
                defaultProps: {
                    variant: 'contained',
                    size: 'small',
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontWeight: 600,
                        textTransform: 'initial',
                        fontSize: '16px',
                        textWrap: 'nowrap',
                        borderRadius: '6px',
                        '&.MuiButton-contained': {
                            padding: '4px 16px',
                            fontWeight: '700',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                            },
                            '&:disabled': {
                                backgroundColor: '#E0E0E0 !important',
                                color: theme.palette.ui.textDisabled,
                                fontWeight: '700',
                                boxShadow: 'none',
                            },
                            '&.MuiLoadingButton-root': {
                                backgroundColor: theme.palette.primary.main,
                            },
                        },
                        '&.MuiButton-outlined': {
                            padding: '3px 16px',
                            color: theme.palette.secondary.main,
                            borderColor: theme.palette.secondary.main,
                            fontWeight: '700',
                            fontSize: '16px',
                            lineHeight: '28px',
                            '&:hover': {
                                backgroundColor: theme.palette.primary[90],
                            },
                            '&:disabled': {
                                backgroundColor: '#FFFFFF',
                                color: theme.palette.ui.textDisabled,
                                borderColor: '#E0E0E0',
                                '& .MuiButton-startIcon': {
                                    '& svg path': {
                                        fill: theme.palette.ui.textDisabled,
                                    },
                                },
                            },
                            '&.MuiLoadingButton-root': {
                                backgroundColor: theme.palette.primary.main,
                                '&:disabled': {
                                    backgroundColor: '#FFFF !important',
                                    color: `${theme.palette.ui.textDisabled} !important`,
                                    borderColor: '#E0E0E0 !important',
                                },
                            },
                            '& .MuiButton-startIcon': {
                                '& svg path': {
                                    fill: theme.palette.secondary.main,
                                },
                            },
                            '&.stroke .MuiButton-startIcon': {
                                '& svg path': {
                                    stroke: theme.palette.secondary.main,
                                },
                            },
                            '& .MuiButton-endIcon': {
                                '& svg path': {
                                    fill: theme.palette.secondary.main,
                                },
                            },
                            '&.stroke .MuiButton-endIcon': {
                                '& svg path': {
                                    stroke: theme.palette.secondary.main,
                                },
                            },
                        },
                        '&.MuiButton-text': {
                            '&:hover': {
                                backgroundColor: 'rgb(68 73 77 / 22%)',
                            },
                        },
                        '&.MuiPickersDay-root.Mui-selected': {
                            backgroundColor: 'red',
                        },
                        '& .MuiButton-startIcon': {
                            [theme.breakpoints.up('md')]: {
                                marginRight: '8px',
                            },
                            [theme.breakpoints.down('md')]: {
                                marginRight: '0px',
                            },
                        },
                    }),
                },
            },
            MuiTreeItem: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '& .MuiTreeItem-content': {
                            backgroundColor: 'unset !important'
                        },
                    }),
                },
            },
        },
    });
};

export default generateTheme;
